import React from 'react';

import { isEmpty } from 'lodash-es';
import { useSetRecoilState } from 'recoil';

import useGetProperties from './app/metadata/hooks/useGetProperties';
import useGetSubProperties from './app/metadata/hooks/useGetSubProperties';
import type { PropertyType } from './state/propertyState';
import { propertyState } from './state/propertyState';
import type { AppLocaleType } from './types/AppLocaleType';
import { APP_LOCALE_TYPE_LIST } from './types/AppLocaleType';

/**
 * 웹에서 쓰는 속성 recoil 세팅.
 *
 * property를 fetch하고 정제합니다.
 */
export default function PropertyProvider() {
  const setProperties = useSetRecoilState(propertyState);

  const { data: aimsCategory } = useGetProperties('AIMS_CATEGORY');
  const { data: categoriesRes } = useGetProperties('AIMS_CATEGORY', ['GROUP']);

  const { data: colorsRes } = useGetProperties('COLOR');

  const { data: neckShoulderRes } = useGetProperties('NECK-SHOULDER');

  const { data: aimsFabricRes } = useGetProperties('AIMS_FABRIC');

  const { data: aimsPatternRes } = useGetProperties('PATTERN');

  const subCategoriesRes = useGetSubProperties(categoriesRes?.properties ?? []);

  React.useEffect(() => {
    if (
      categoriesRes == null ||
      isEmpty(subCategoriesRes) ||
      colorsRes == null ||
      aimsCategory == null ||
      neckShoulderRes == null ||
      aimsFabricRes == null ||
      aimsPatternRes == null
    ) {
      return;
    }

    const categories = categoriesRes.properties;

    const data: Partial<Record<AppLocaleType, PropertyType>> = APP_LOCALE_TYPE_LIST.reduce(
      (curr: Partial<Record<AppLocaleType, PropertyType>>, locale) => {
        return {
          ...curr,
          [locale]: {
            aimsCategory: {
              raw: [],
              values: {},
              ids: [],
              groupIDs: [],
            },
            color: {
              raw: [],
              values: {},
              ids: [],
            },
            neckShoulder: {
              raw: [],
              values: {},
              ids: [],
            },
            aimsFabric: {
              raw: [],
              values: {},
              ids: [],
            },
            aimsPattern: {
              raw: [],
              values: {},
              ids: [],
            },
          } satisfies PropertyType,
        };
      },
      {},
    );

    // raw 데이터 카피.
    APP_LOCALE_TYPE_LIST.forEach(locale => {
      data[locale]!.aimsCategory!.raw = aimsCategory.properties;
      data[locale]!.color!.raw = colorsRes.properties;
      data[locale]!.neckShoulder!.raw = neckShoulderRes.properties;
      data[locale]!.aimsFabric!.raw = aimsFabricRes.properties;
      data[locale]!.aimsPattern!.raw = aimsPatternRes.properties;
    });

    // 대분류 추가.
    APP_LOCALE_TYPE_LIST.forEach(locale => {
      data[locale]!.aimsCategory!.groupIDs = categories.map(v => v.code);

      categories.forEach(category => {
        const value = category[locale];
        if (value != null) {
          data[locale]!.aimsCategory!.values[category.code] = {
            name: value,
          };
          data[locale]!.aimsCategory!.ids.push(category.code);
        }
      });
    });

    // 소분류 추가 및 대분류-소분류 관계 구성.
    subCategoriesRes.data.forEach(subgroup => {
      APP_LOCALE_TYPE_LIST.forEach(locale => {
        // 대분류에 소분류 리스트 추가.
        const parentCategory = data[locale]?.aimsCategory.values[subgroup.parentCode];

        if (parentCategory) {
          parentCategory.children = subgroup.children.map(v => v.code);
        }

        subgroup.children.forEach(childProps => {
          const value = childProps[locale];
          if (value != null) {
            data[locale]!.aimsCategory!.values[childProps.code] = {
              name: value,
              parent: subgroup.parentCode,
              children: [],
            };
            data[locale]!.aimsCategory!.ids.push(childProps.code);
          }
        });
      });
    });

    colorsRes.properties.forEach(property => {
      APP_LOCALE_TYPE_LIST.forEach(locale => {
        const value = property[locale];
        if (value != null) {
          data[locale]!.color!.values[property.code] = {
            name: value,
          };
          data[locale]!.color!.ids.push(property.code);
        }
      });
    });

    neckShoulderRes.properties.forEach(property => {
      APP_LOCALE_TYPE_LIST.forEach(locale => {
        const value = property[locale];
        if (value != null) {
          data[locale]!.neckShoulder!.values[property.code] = {
            name: value,
          };
          data[locale]!.neckShoulder!.ids.push(property.code);
        }
      });
    });

    aimsFabricRes.properties.forEach(property => {
      APP_LOCALE_TYPE_LIST.forEach(locale => {
        const value = property[locale];
        if (value != null) {
          data[locale]!.aimsFabric!.values[property.code] = {
            name: value,
          };
          data[locale]!.aimsFabric!.ids.push(property.code);
        }
      });
    });

    const appLocale = APP_LOCALE_TYPE_LIST.find(v => v === 'ko');

    if (appLocale == null) {
      return;
    }
    const resData = data[appLocale];

    if (resData) {
      setProperties(resData);
    }
  }, [
    aimsCategory,
    aimsFabricRes,
    aimsPatternRes,
    categoriesRes,
    colorsRes,
    neckShoulderRes,
    setProperties,
    subCategoriesRes,
  ]);

  return null;
}
